// src/pages/student/TopicSimilarityReport.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    InputAdornment,
    Tooltip,
    Alert,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Fade,
    Zoom,
    Card,
    CardContent,
    LinearProgress,
    Avatar,
    Stack,
    Rating,
    useMediaQuery
} from '@mui/material';
import {
    Search,
    Refresh as RefreshIcon,
    Visibility as ViewIcon,
    Close as CloseIcon,
    FilterList as FilterIcon,
    Today as TodayIcon,
    CheckCircle,
    Cancel,
    Warning,
    Schedule,
    Psychology,
    MenuBook,
    Science,
    TrendingUp,
    TrendingDown,
    TrendingFlat,
    Analytics,
    Assessment,
    Description,
    Lightbulb,
    DateRange,
    ExpandMore,
    ClearAll,
    PictureAsPdf,
    GetApp,
    Print,
    ThumbUp,
    ThumbDown,
    Info,
    Star,
    BarChart,
    Timeline
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APITopicSimilarityList,
    APITopicSimilarityShow,
    APITopicSimilarityGenerateReport
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    recordsCount, 
    recordsPerPage,
    getUserDetails
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
// NOTE: The component file is still named StudentBar.js
// but it now renders "Researcher Portal" as its visible title.
import StudentBar from '../../components/StudentBar';
import authService from '../../apiServices/authService';
import { FullWidthContainer } from '../../components/FullWidthContainer';

// Novelty indicator options
const noveltyOptions = [
    { id: 'high', name: 'High Novelty', color: 'success', icon: <TrendingUp />, description: 'Highly original topic with significant new insights' },
    { id: 'medium', name: 'Medium Novelty', color: 'info', icon: <TrendingFlat />, description: 'Moderately original with some new perspectives' },
    { id: 'low', name: 'Low Novelty', color: 'warning', icon: <TrendingDown />, description: 'Limited originality, needs enhancement' },
    { id: 'needs_revision', name: 'Needs Revision', color: 'error', icon: <Warning />, description: 'Topic requires significant revision' }
];

// Analysis status options
const statusOptions = [
    { id: 'pending', name: 'Pending', color: 'warning', icon: <Schedule /> },
    { id: 'processing', name: 'Processing', color: 'info', icon: <CircularProgress size={16} /> },
    { id: 'completed', name: 'Completed', color: 'success', icon: <CheckCircle /> },
    { id: 'failed', name: 'Failed', color: 'error', icon: <Cancel /> }
];

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const TopicSimilarityReport = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [pageState, setPageState] = useState({ pageData: [] });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const isMounted = useRef(true);

    // Handle logout
    const handleLogout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    // Filters
    const [filters, setFilters] = useState({
        analysis_status: '',
        novelty_indicator: '',
        dateFrom: '',
        dateTo: ''
    });

    // Dialog states
    const [filterOptions, setFilterOptions] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [reportLoading, setReportLoading] = useState(false);

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchData();
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const result = await apiService.list(APITopicSimilarityList);

            if (result.success && isMounted.current) {
                const results = Array.isArray(result.data) ? result.data.reverse() : [];
                setPageState({ pageData: results });
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const handleOpenView = async (record) => {
        setSelectedRecord(record);
        setOpenView(true);
        
        // Fetch detailed data if needed
        try {
            const result = await apiService.show(APITopicSimilarityShow, record.id);
            if (result.success && result.data) {
                setSelectedRecord(result.data);
            }
        } catch (error) {
            console.error("Error fetching details:", error);
        }
    };

    const handleCloseView = () => {
        setOpenView(false);
        setSelectedRecord(null);
    };

    const handleGenerateReport = async () => {
        if (!selectedRecord) return;
        
        setReportLoading(true);
        try {
            const formData = new FormData();
            formData.append('updateId', selectedRecord.id);
            formData.append('user_id', getUserDetails("Id"));
            
            const result = await apiService.update(APITopicSimilarityGenerateReport, formData);
            
            if (result && result.success) {
                showToast("Success", "Report generated successfully", "success");
                fetchData();
            }
        } catch (error) {
            showToast("Error", "Failed to generate report", "error");
        } finally {
            setReportLoading(false);
        }
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleCloseOptions = () => setFilterOptions(false);

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters(prev => ({ ...prev, dateFrom: today, dateTo: today }));
        handleCloseOptions();
    };

    const clearFilters = () => {
        setFilters({
            analysis_status: '',
            novelty_indicator: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    };

    // Filtered data
    const filteredData = pageState.pageData.filter(record => {
        const matchesSearch = searchTerm === '' ||
            record.result_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.topic_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.clustering_group?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = !filters.analysis_status || record.analysis_status === filters.analysis_status;
        const matchesNovelty = !filters.novelty_indicator || record.novelty_indicator === filters.novelty_indicator;

        let matchesDate = true;
        if (filters.dateFrom && record.analysis_date) {
            matchesDate = matchesDate && record.analysis_date.split('T')[0] >= filters.dateFrom;
        }
        if (filters.dateTo && record.analysis_date) {
            matchesDate = matchesDate && record.analysis_date.split('T')[0] <= filters.dateTo;
        }

        return matchesSearch && matchesStatus && matchesNovelty && matchesDate;
    });

    // Pagination
    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Get novelty display
    const getNoveltyDisplay = (indicator) => {
        const option = noveltyOptions.find(n => n.id === indicator);
        return option || noveltyOptions[2];
    };

    if (loading) {
        return <LoadingState message="Loading Similarity Reports..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchData} title="Unable to Load Similarity Reports" variant="research" fullPage retryText="Retry Loading" />;
    }

    return (
        <>
            <FullWidthContainer >
                <Box sx={{backgroundColor: 'white',borderRadius: 3 }}>
                    <StudentBar notificationCount={20} />
                    {/* Header Section */}
                    <Paper elevation={2} sx={{ 
                            p: { xs: 2, sm: 3 }, 
                            mb: { xs: 2, sm: 3 }, 
                            borderRadius: 3,
                            backgroundColor: 'white',
                        }}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2, 
                            flexWrap: 'wrap',
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Typography variant="h6" sx={{
                                fontWeight: 'bold',
                                color: theme.palette.primary.main,
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                <Analytics /> Topic Similarity Reports ({recordsCount(pageState.pageData.length)})
                            </Typography>

                            <Box sx={{ 
                                display: 'flex', 
                                gap: 1, 
                                flexShrink: 0,
                                width: { xs: '100%', sm: 'auto' },
                                justifyContent: { xs: 'center', sm: 'flex-start' }
                            }}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<FilterIcon />}
                                    onClick={() => setFilterOptions(true)}
                                    sx={{ 
                                        textTransform: 'none', 
                                        borderRadius: '8px',
                                        flex: { xs: 1, sm: 'none' }
                                    }}
                                >
                                    View Options
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="info"
                                    startIcon={<RefreshIcon />}
                                    onClick={fetchData}
                                    sx={{ 
                                        textTransform: 'none', 
                                        borderRadius: '8px',
                                        flex: { xs: 1, sm: 'none' }
                                    }}
                                >
                                    Refresh
                                </Button>
                            </Box>

                            {/* Search Bar */}
                            <Paper sx={{
                                width: { xs: '100%', sm: '300px' },
                                p: '2px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                marginLeft: { xs: 0, sm: 'auto' },
                                borderRadius: '8px',
                                backgroundColor: theme.palette.grey[50],
                            }}>
                                <TextField
                                    fullWidth
                                    variant="standard"
                                    placeholder="Search by ID, topic, or group..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        disableUnderline: true,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search sx={{ color: theme.palette.grey[600] }} />
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            '& .MuiInputBase-input': {
                                                padding: '10px 0',
                                            }
                                        }
                                    }}
                                    size="small"
                                />
                            </Paper>
                        </Box>

                        {/* Active Filters */}
                        {(filters.analysis_status || filters.novelty_indicator || filters.dateFrom || filters.dateTo) && (
                            <Box sx={{ 
                                display: 'flex', 
                                gap: 1, 
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                mt: 2,
                                pt: 2,
                                borderTop: '1px solid',
                                borderColor: 'divider'
                            }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    Active filters:
                                </Typography>
                                {filters.analysis_status && (
                                    <Chip
                                        label={`Status: ${statusOptions.find(s => s.id === filters.analysis_status)?.name}`}
                                        onDelete={() => setFilters(prev => ({ ...prev, analysis_status: '' }))}
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                                {filters.novelty_indicator && (
                                    <Chip
                                        label={`Novelty: ${getNoveltyDisplay(filters.novelty_indicator).name}`}
                                        onDelete={() => setFilters(prev => ({ ...prev, novelty_indicator: '' }))}
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                                {filters.dateFrom && (
                                    <Chip
                                        label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                        onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                                {filters.dateTo && (
                                    <Chip
                                        label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                        onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                                <Button
                                    size="small"
                                    onClick={clearFilters}
                                    startIcon={<ClearAll />}
                                    sx={{ ml: 1 }}
                                >
                                    Clear All
                                </Button>
                            </Box>
                        )}
                    </Paper>
                </Box>
                {/* Reports Table */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    <Paper elevation={2} sx={{ 
                        borderRadius: 3, 
                        overflow: 'auto',
                        backgroundColor: 'white',
                    }}>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table sx={{ minWidth: { xs: 700, sm: 800, md: 1000 } }}>
                                <TableHead>
                                    <TableRow sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        '& th': {
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' }
                                        }
                                    }}>
                                        <TableCell>#</TableCell>
                                        <TableCell>Result ID</TableCell>
                                        <TableCell>Topic</TableCell>
                                        <TableCell>Originality</TableCell>
                                        <TableCell>Novelty</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Analysis Date</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((record, index) => {
                                        const novelty = getNoveltyDisplay(record.novelty_indicator);
                                        const status = statusOptions.find(s => s.id === record.analysis_status);
                                        
                                        return (
                                            <TableRow
                                                key={record.id}
                                                hover
                                                sx={{
                                                    '&:nth-of-type(odd)': {
                                                        backgroundColor: theme.palette.action.hover
                                                    }
                                                }}
                                            >
                                                <TableCell>
                                                    <Chip label={page * rowsPerPage + index + 1} color="primary" size="small" />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={record.result_id} color="info" size="small" variant="outlined" />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Description fontSize="small" color="primary" />
                                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            {record.topic_title?.substring(0, isMobile ? 30 : 50)}...
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                                            <CircularProgress
                                                                variant="determinate"
                                                                value={record.originality_score || 0}
                                                                size={isMobile ? 30 : 40}
                                                                thickness={4}
                                                                color={record.originality_score >= 70 ? 'success' : record.originality_score >= 50 ? 'warning' : 'error'}
                                                            />
                                                            <Box
                                                                sx={{
                                                                    top: 0,
                                                                    left: 0,
                                                                    bottom: 0,
                                                                    right: 0,
                                                                    position: 'absolute',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <Typography variant="caption" component="div" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                                                                    {record.originality_score || 0}%
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        {!isMobile && (
                                                            <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                                                                {record.formatted_originality_score}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={novelty.icon}
                                                        label={isMobile ? '' : novelty.name}
                                                        color={novelty.color}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={status?.icon}
                                                        label={isMobile ? '' : status?.name}
                                                        color={status?.color}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <DateRange fontSize="small" color="info" />
                                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                            {formatCreatedAtDate(record.analysis_date)}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title="View Details">
                                                        <IconButton onClick={() => handleOpenView(record)} color="primary" size="small">
                                                            <ViewIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{ mt: 2 }}
                        />

                        {filteredData.length === 0 && (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                py: { xs: 6, sm: 8 },
                                color: 'text.secondary'
                            }}>
                                <Analytics sx={{ fontSize: { xs: 48, sm: 60 }, mb: 2 }} />
                                <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    No similarity reports found
                                </Typography>
                                <Typography variant="body2">Try adjusting your search query</Typography>
                            </Box>
                        )}
                    </Paper>
                </motion.div>

                {/* Filter Options Dialog */}
                <Dialog
                    open={filterOptions}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="sm"
                    disableEscapeKeyDown
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseOptions();
                        }
                    }}
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ textAlign: 'center', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Filter Options
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: { xs: 1, sm: 2 } }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<RefreshIcon />}
                                onClick={clearFilters}
                                sx={{ textTransform: 'none' }}
                                size={isMobile ? "small" : "medium"}
                            >
                                Show All Reports
                            </Button>

                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<TodayIcon />}
                                onClick={applyTodayFilter}
                                sx={{ textTransform: 'none' }}
                                size={isMobile ? "small" : "medium"}
                            >
                                Show Today's Reports
                            </Button>

                            <Divider>OR</Divider>

                            <Typography variant="subtitle1" sx={{ textAlign: 'center', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                Filter by Analysis Status
                            </Typography>

                            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                                <InputLabel>Analysis Status</InputLabel>
                                <Select
                                    name="analysis_status"
                                    value={filters.analysis_status}
                                    onChange={handleFilterChange}
                                    label="Analysis Status"
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    {statusOptions.map((status) => (
                                        <MenuItem key={status.id} value={status.id}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {status.icon}
                                                {status.name}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Divider>OR</Divider>

                            <Typography variant="subtitle1" sx={{ textAlign: 'center', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                Filter by Novelty Level
                            </Typography>

                            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                                <InputLabel>Novelty Indicator</InputLabel>
                                <Select
                                    name="novelty_indicator"
                                    value={filters.novelty_indicator}
                                    onChange={handleFilterChange}
                                    label="Novelty Indicator"
                                >
                                    <MenuItem value="">All Levels</MenuItem>
                                    {noveltyOptions.map((novelty) => (
                                        <MenuItem key={novelty.id} value={novelty.id}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {novelty.icon}
                                                {novelty.name}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Divider>OR</Divider>

                            <Typography variant="subtitle1" sx={{ textAlign: 'center', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                Filter by Date Range
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="From Date"
                                        type="date"
                                        name="dateFrom"
                                        value={filters.dateFrom}
                                        onChange={handleFilterChange}
                                        InputLabelProps={{ shrink: true }}
                                        size={isMobile ? "small" : "medium"}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="To Date"
                                        type="date"
                                        name="dateTo"
                                        value={filters.dateTo}
                                        onChange={handleFilterChange}
                                        InputLabelProps={{ shrink: true }}
                                        size={isMobile ? "small" : "medium"}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button color="error" onClick={handleCloseOptions} variant="contained" size={isMobile ? "small" : "medium"}>
                            Close
                        </Button>
                        <Button onClick={handleCloseOptions} color="primary" variant="contained" size={isMobile ? "small" : "medium"}>
                            Apply Filters
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* View Details Dialog */}
                <Dialog
                    open={openView}
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseView();
                        }
                    }}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="lg"
                    disableEscapeKeyDown
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        textAlign: 'center',
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}>
                        <b>Topic Similarity Analysis Report</b>
                    </DialogTitle>
                    <DialogContent>
                        {selectedRecord && (
                            <Box sx={{ p: { xs: 1, sm: 2 } }}>
                                {/* Header */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center', 
                                    mb: 3, 
                                    flexWrap: 'wrap', 
                                    gap: 2,
                                    flexDirection: { xs: 'column', sm: 'row' }
                                }}>
                                    <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                                        <Typography variant="h6" color="primary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                            {selectedRecord.result_id}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            {selectedRecord.topic_title}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                                        <Chip
                                            icon={statusOptions.find(s => s.id === selectedRecord.analysis_status)?.icon}
                                            label={statusOptions.find(s => s.id === selectedRecord.analysis_status)?.name}
                                            color={statusOptions.find(s => s.id === selectedRecord.analysis_status)?.color}
                                            size="small"
                                        />
                                        {!selectedRecord.report_generated && selectedRecord.analysis_status === 'completed' && (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<PictureAsPdf />}
                                                onClick={handleGenerateReport}
                                                disabled={reportLoading}
                                            >
                                                {reportLoading ? <CircularProgress size={20} /> : 'Generate Report'}
                                            </Button>
                                        )}
                                        {selectedRecord.report_generated && (
                                            <Chip icon={<CheckCircle />} label="Report Generated" color="success" size="small" />
                                        )}
                                    </Box>
                                </Box>

                                {/* Novelty Score Card */}
                                <Card sx={{ mb: 3, bgcolor: theme.palette.primary.light + '10' }}>
                                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                        <Grid container spacing={3} alignItems="center">
                                            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                                                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                                    <CircularProgress
                                                        variant="determinate"
                                                        value={selectedRecord.originality_score || 0}
                                                        size={isMobile ? 100 : 120}
                                                        thickness={4}
                                                        color={selectedRecord.originality_score >= 70 ? 'success' : selectedRecord.originality_score >= 50 ? 'warning' : 'error'}
                                                    />
                                                    <Box
                                                        sx={{
                                                            top: 0,
                                                            left: 0,
                                                            bottom: 0,
                                                            right: 0,
                                                            position: 'absolute',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexDirection: 'column'
                                                        }}
                                                    >
                                                        <Typography variant="h4" component="div" color="text.primary" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                                                            {selectedRecord.originality_score || 0}%
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Originality
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} md={8}>
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: 2, 
                                                    mb: 2,
                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                    textAlign: { xs: 'center', sm: 'left' }
                                                }}>
                                                    {(() => {
                                                        const novelty = getNoveltyDisplay(selectedRecord.novelty_indicator);
                                                        return (
                                                            <>
                                                                <Avatar sx={{ 
                                                                    bgcolor: `${novelty.color}.main`, 
                                                                    width: { xs: 48, sm: 56 }, 
                                                                    height: { xs: 48, sm: 56 } 
                                                                }}>
                                                                    {novelty.icon}
                                                                </Avatar>
                                                                <Box>
                                                                    <Typography variant="h5" sx={{ 
                                                                        fontWeight: 'bold',
                                                                        fontSize: { xs: '1rem', sm: '1.5rem' }
                                                                    }}>
                                                                        {novelty.name}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                                        {novelty.description}
                                                                    </Typography>
                                                                </Box>
                                                            </>
                                                        );
                                                    })()}
                                                </Box>
                                                {selectedRecord.clustering_group && (
                                                    <Chip
                                                        icon={<Assessment />}
                                                        label={`Cluster Group: ${selectedRecord.clustering_group}`}
                                                        color="info"
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                )}
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>

                                {/* Similar Topics */}
                                {selectedRecord.similar_topics && selectedRecord.similar_topics.length > 0 && (
                                    <Accordion defaultExpanded>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Timeline color="warning" />
                                                <Typography variant="subtitle1" sx={{ 
                                                    fontWeight: 'bold',
                                                    fontSize: { xs: '0.9rem', sm: '1rem' }
                                                }}>
                                                    Similar Topics Found
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                {selectedRecord.similar_topics.map((topic, idx) => (
                                                    <Grid item xs={12} key={idx}>
                                                        <Paper sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'grey.50' }}>
                                                            <Box sx={{ 
                                                                display: 'flex', 
                                                                justifyContent: 'space-between', 
                                                                alignItems: 'center', 
                                                                flexWrap: 'wrap', 
                                                                gap: 2,
                                                                flexDirection: { xs: 'column', sm: 'row' }
                                                            }}>
                                                                <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
                                                                    <Typography variant="subtitle2" sx={{ 
                                                                        fontWeight: 'bold',
                                                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                                                    }}>
                                                                        {topic.title}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Topic ID: {topic.topic_id}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ minWidth: { xs: '100%', sm: 150 } }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <LinearProgress
                                                                            variant="determinate"
                                                                            value={topic.similarity_percentage}
                                                                            sx={{ flex: 1, height: 8, borderRadius: 4 }}
                                                                            color={topic.similarity_percentage >= 70 ? 'error' : topic.similarity_percentage >= 50 ? 'warning' : 'info'}
                                                                        />
                                                                        <Typography variant="body2" fontWeight="bold">
                                                                            {topic.similarity_percentage}%
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        </Paper>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {/* Extracted Keywords */}
                                {selectedRecord.extracted_keywords && selectedRecord.extracted_keywords.length > 0 && (
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Science color="info" />
                                                <Typography variant="subtitle1" sx={{ 
                                                    fontWeight: 'bold',
                                                    fontSize: { xs: '0.9rem', sm: '1rem' }
                                                }}>
                                                    Extracted Keywords
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {selectedRecord.extracted_keywords.map((keyword, idx) => (
                                                    <Chip
                                                        key={idx}
                                                        label={keyword}
                                                        color="primary"
                                                        variant="outlined"
                                                        icon={<Lightbulb />}
                                                        size="small"
                                                    />
                                                ))}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {/* Recommendations */}
                                {selectedRecord.recommendations && (
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Lightbulb color="success" />
                                                <Typography variant="subtitle1" sx={{ 
                                                    fontWeight: 'bold',
                                                    fontSize: { xs: '0.9rem', sm: '1rem' }
                                                }}>
                                                    Recommendations
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography variant="body2" sx={{ 
                                                whiteSpace: 'pre-wrap',
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                            }}>
                                                {selectedRecord.recommendations}
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {/* Analysis Notes */}
                                {selectedRecord.analysis_notes && (
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Info color="action" />
                                                <Typography variant="subtitle1" sx={{ 
                                                    fontWeight: 'bold',
                                                    fontSize: { xs: '0.9rem', sm: '1rem' }
                                                }}>
                                                    Analysis Notes
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography variant="body2" sx={{ 
                                                whiteSpace: 'pre-wrap',
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                            }}>
                                                {selectedRecord.analysis_notes}
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {/* Dates */}
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <DateRange color="action" />
                                            <Typography variant="subtitle1" sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: { xs: '0.9rem', sm: '1rem' }
                                            }}>
                                                Timeline
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    <b>Analysis Date:</b>
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                                                    {formatCreatedAtDate(selectedRecord.analysis_date)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    <b>Last Updated:</b>
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                                                    {formatCreatedAtDate(selectedRecord.updated_at)}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button color="error" onClick={handleCloseView} variant="contained" size={isMobile ? "small" : "medium"}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default TopicSimilarityReport;